import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen flex flex-col">
      <MarketingHeader />
      <div className="flex-1 container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold mb-8 tracking-wide">Privacy Policy</h1>
        
        <div className="prose prose-slate dark:prose-invert max-w-none">
          <p className="text-muted-foreground mb-6">
            <strong>Last Updated:</strong> June 29, 2026
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">1. Information We Collect</h2>
            <p className="text-muted-foreground mb-4">
              Fill The Trucks collects information to provide and improve our technology platform. Fill The Trucks is not a broker, carrier, or freight forwarder and does not provide transportation services. The information we collect includes:
            </p>
            
            <h3 className="text-xl font-semibold mb-3">Account Information</h3>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li>Name, email address, and contact details</li>
              <li>Company name and business information</li>
              <li>Membership type and subscription payment information</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">Freight and Communication Data</h3>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li>Load details including origin, destination, weight, and dimensions</li>
              <li>Truck availability information</li>
              <li>Communication between users on the platform</li>
              <li>Records of user-generated communications, offers, and acceptances exchanged through the platform. Fill The Trucks is not a party to any agreement between users.</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">Documents and Attachments</h3>
            <p className="text-muted-foreground mb-4">
              Documents and attachments uploaded through the messaging system are shared at the direction of users. Fill The Trucks does not review, approve, or validate such documents.
            </p>

            <h3 className="text-xl font-semibold mb-3">Automatically Collected Information</h3>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li>Device and browser information</li>
              <li>IP address and location data</li>
              <li>Usage patterns and preferences</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">Carrier and Broker Verification</h3>
            <p className="text-muted-foreground mb-4">
              We may collect and verify carrier and broker registration information, including MC and DOT numbers, using publicly available records from the Federal Motor Carrier Safety Administration (FMCSA). Verification confirms registration status only and does not constitute endorsement, recommendation, or evaluation of safety, insurance, or service quality. Verification data reflects information available at the time of review and may change.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">2. How We Use Your Information</h2>
            <p className="text-muted-foreground mb-4">
              We use the collected information for the following purposes:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li>Providing and maintaining our technology platform</li>
              <li>Providing a technology platform that allows users to independently discover, communicate, and exchange information with other users</li>
              <li>Processing subscription fees and managing user memberships. Fill The Trucks does not process or facilitate payments between users.</li>
              <li>Sending service updates, notifications, and customer support</li>
              <li>Improving platform features and user experience</li>
              <li>Preventing fraud and ensuring platform security</li>
              <li>Complying with legal obligations and regulations</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">What We Do Not Do</h3>
            <p className="text-muted-foreground mb-4">
              Fill The Trucks does not arrange transportation, negotiate rates, select or recommend carriers, guarantee performance, process freight payments, or act as a party to any shipment agreement. All transportation contracts and agreements are made directly between users.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">3. Information Sharing</h2>
            <p className="text-muted-foreground mb-4">
              We respect your privacy and do not sell your personal information. We may share your information in the following limited circumstances:
            </p>
            
            <h3 className="text-xl font-semibold mb-3">With Other Platform Users</h3>
            <p className="text-muted-foreground mb-4">
              When you post loads or truck availability, relevant information is visible to other users. You control what information you share through direct messaging. Any transactions or agreements are made directly between users; Fill The Trucks is not a party to such arrangements.
            </p>

            <h3 className="text-xl font-semibold mb-3">Service Providers</h3>
            <p className="text-muted-foreground mb-4">
              We share information with trusted third-party service providers who assist in operating our platform, including payment processors (Stripe), hosting services, analytics providers, and error tracking (Sentry).
            </p>

            <h3 className="text-xl font-semibold mb-3">Legal Requirements</h3>
            <p className="text-muted-foreground mb-4">
              We may disclose information when required by law, to respond to legal processes, or to protect the rights, property, or safety of Fill The Trucks, our users, or others.
            </p>

            <h3 className="text-xl font-semibold mb-3">Business Transfers</h3>
            <p className="text-muted-foreground mb-4">
              If Fill The Trucks is involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">4. Data Security</h2>
            <p className="text-muted-foreground mb-4">
              We implement industry-standard security measures to protect your information from unauthorized access, disclosure, alteration, or destruction. This includes:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li>Encrypted data transmission using SSL/TLS</li>
              <li>Secure password storage and authentication</li>
              <li>Regular security assessments and monitoring</li>
              <li>Restricted access to personal information</li>
            </ul>
            <p className="text-muted-foreground mb-4">
              However, no method of transmission or storage is 100% secure. We cannot guarantee absolute security of your information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">5. Your Rights and Choices</h2>
            <p className="text-muted-foreground mb-4">
              You have the following rights regarding your personal information:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information in your account</li>
              <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
              <li><strong>Data Portability:</strong> Request a copy of your data in a portable format</li>
            </ul>
            <p className="text-muted-foreground mb-4">
              To exercise these rights, please contact us at hello@fillthetrucks.com.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">6. Cookies and Tracking</h2>
            <p className="text-muted-foreground mb-4">
              We use cookies and similar tracking technologies to enhance your experience on our platform. These technologies help us:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li>Remember your preferences and login information</li>
              <li>Analyze platform usage and performance</li>
              <li>Provide personalized content and recommendations</li>
            </ul>
            <p className="text-muted-foreground mb-4">
              We do not use personal information for third-party targeted advertising.
            </p>
            <p className="text-muted-foreground mb-4">
              You can control cookie preferences through your browser settings, though some features may not function properly if cookies are disabled.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">7. Data Retention</h2>
            <p className="text-muted-foreground mb-4">
              We retain your information for as long as necessary to provide our services and fulfill the purposes outlined in this policy. When you delete your account, we will delete or anonymize your personal information within 90 days, except where retention is required for legal, regulatory, or security purposes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">8. Children's Privacy</h2>
            <p className="text-muted-foreground mb-4">
              Fill The Trucks is intended for business use only and is not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child, we will take steps to delete such information promptly.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">9. International Data Transfers</h2>
            <p className="text-muted-foreground mb-4">
              While the platform is intended for use in the United States, information may be processed by service providers located in other countries. We take appropriate safeguards to ensure that your personal information remains protected in accordance with this Privacy Policy when transferred internationally.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">10. Changes to This Policy</h2>
            <p className="text-muted-foreground mb-4">
              We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of material changes by posting the updated policy on our website and updating the "Last Updated" date. Your continued use of the platform after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">11. Contact Us</h2>
            <p className="text-muted-foreground mb-4">
              If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
            </p>
            <ul className="list-none text-muted-foreground mb-4">
              <li><strong>Email:</strong> hello@fillthetrucks.com</li>
            </ul>
          </section>
        </div>
      </div>
      <MarketingFooter />
    </div>
  );
}
